/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useEffect, useRef, useState, useCallback } from 'react';

const POLL_INTERVAL = 60_000;
const IDLE_TIMEOUT = 5 * 60 * 1000;

declare const __APP_BUILD_ID__: string;

export function useAutoUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const updateAvailableRef = useRef(false);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    const markActivity = () => { lastActivityRef.current = Date.now(); };
    window.addEventListener('click', markActivity, { passive: true });
    window.addEventListener('keydown', markActivity, { passive: true });
    window.addEventListener('scroll', markActivity, { passive: true });
    return () => {
      window.removeEventListener('click', markActivity);
      window.removeEventListener('keydown', markActivity);
      window.removeEventListener('scroll', markActivity);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch('/version.json?_=' + Date.now());
        if (!res.ok) return;
        const { buildId } = await res.json();
        if (buildId && buildId !== __APP_BUILD_ID__) {
          setUpdateAvailable(true);
          updateAvailableRef.current = true;
        }
      } catch {
        /* silent - network error should not affect UX */
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!updateAvailable) return;

    const idleCheck = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs >= IDLE_TIMEOUT && updateAvailableRef.current) {
        window.location.reload();
      }
    }, 10_000);

    return () => clearInterval(idleCheck);
  }, [updateAvailable]);

  const onRouteChange = useCallback(() => {
    if (updateAvailableRef.current) {
      window.location.reload();
    }
  }, []);

  return { updateAvailable, onRouteChange };
}
